'use client';

import { c as _c } from "react/compiler-runtime";
import { CloseButton, Dialog, DialogPanel } from '@headlessui/react';
import CloseWhite from '@public/images/close_white.svg';
import { AnimatePresence, motion } from 'motion/react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import Script from 'next/script';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { getSession } from '@src/actions/auth.actions';
import { ampli as ampliDigital } from '@src/ampli-web-digital';
import { VideoWaiver } from '@src/components/VideoPlayer/VideoWaiver';
import { configPublic } from '@src/config.public';
import { useClearVideoQueryParams } from '@src/hooks/useClearVideoQueryParams';
import { usePostWorkoutExperienceController } from '@src/hooks/usePostWorkoutExperience';
import { useQuerySelector } from '@src/hooks/useQuerySelector';
import { useSession } from '@src/hooks/useSession';
import { useVideoWaiver } from '@src/hooks/useVideoWaiver';
import { useLocale } from '@src/i18n/LocaleProvider';
import { type PlayVideoAnalytics, getVideoPlayerScreenName, getVideoPlayerTabName } from '@src/utils/analytics-helpers.util';
import { omitNullish } from '@src/utils/object.util';
import { instrumentAction } from "@src/utils/instrumentation";
export type { PlayVideoAnalytics };
declare global {
  interface Window {
    WebVideoPlayerDOM: {
      launch: (props: LaunchVideoPlayerParams) => Promise<any>;
      destroy: (instanceId?: string) => void;
    };
  }
}
type CallbackFn = () => void;
interface VideoCallbacks {
  onClose?: CallbackFn;
  onPlay?: CallbackFn;
  onPause?: CallbackFn;
  onSeek?: CallbackFn;
  closeCallback?: CallbackFn;
  onAnyAction?: (action: {
    type: string;
    payload: any;
  }) => void;
  onProgress?: (progress: number) => void;
  getAccessToken?: () => Promise<{
    accessToken: string | undefined;
    tokenType?: string;
  }>;
}
export interface PlayVideoProps extends VideoCallbacks {
  videoGUID: string;
  duration?: number;
  entityId?: string;
  dayId?: string;
  userGUID?: string;
  recommendationId?: string;
  autoplay?: boolean;
  selector?: string;
  skipPromoScreen?: boolean;
  isPromoVideo?: boolean;
  isBODi?: boolean;
  videoBrandCode?: string;
  /** Analytics metadata for the centralized play video event */
  analytics?: PlayVideoAnalytics;
}

/**
 * Options for the high-level playEntity() function.
 * Omits fields that are derived directly from the CoCoA entity.
 * The analytics payload must be created via getPlayerAnalytics() to enforce
 * per-scenario required fields.
 */
export type PlayEntityOptions = Omit<PlayVideoProps, 'videoGUID' | 'analytics' | 'entityId' | 'recommendationId' | 'isPromoVideo'> & {
  analytics?: PlayVideoAnalytics;
};
interface VideoPlayerContextType {
  playVideo: (playVideoProps: PlayVideoProps) => Promise<void>;
  playEntity: (content: CocoApi.PlayableEntity | CocoApi.CustomCollectionEntity, options?: PlayEntityOptions) => Promise<void>;
  setScriptUrl: (url: string) => void;
  clearVideoQueryParams: () => void;
  isVideoPlayerOpen: boolean;
}
interface LaunchVideoPlayerParams {
  selector: string;
  props: Omit<PlayVideoProps, 'analytics'> & {
    /** Player callback fired when playback ends, before closeCallback. */
    onStop?: () => void;
  };
}
const VIDEO_PLAYER_CONTAINER_ID = 'video-player-container';
const TEN_MINUTE_TRAINER_BRAND_CODE = 'TM';
const PLAYER_FULLSCREEN_OVERLAY_MEDIA_QUERY = '(max-width: 768px), (orientation: landscape) and (hover: none) and (pointer: coarse) and (max-height: 768px)';
interface ExitVideoSession {
  videoGUID: string;
  duration: number;
  analytics?: PlayVideoAnalytics;
}
const VideoPlayerContext = createContext<VideoPlayerContextType | undefined>(undefined);
const getVideoBrandCode = (args: {
  videoBrandCode?: string;
  brandCode?: string;
  isBODi?: boolean;
}) =>
// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
args.videoBrandCode || args.brandCode || (args.isBODi ? TEN_MINUTE_TRAINER_BRAND_CODE : undefined);
export const VideoPlayerProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const {
    user
  } = useSession();
  const {
    setPostWorkoutEntityId,
    reportVideoProgress,
    startPostWorkout
  } = usePostWorkoutExperienceController();
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [playerInstanceId, setPlayerInstanceId] = useState<string | null>(null);
  const [scriptUrl, setScriptUrl] = useState<string | undefined>(configPublic.videoPlayer?.scriptUrl);
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);
  const [launchVideoParams, setLaunchVideoParams] = useState<LaunchVideoPlayerParams | null>(null);
  const launchVideoPlayerPromise = useRef<Promise<void>>(null);
  const exitVideoSessionRef = useRef<ExitVideoSession | null>(null);
  const lastProgressRef = useRef(0);
  const isCompletedRef = useRef(false);
  const containerElement = useQuerySelector(`#${VIDEO_PLAYER_CONTAINER_ID}`);
  const [isCloseButtonVisible, setIsCloseButtonVisible] = useState(true);
  const {
    locale
  } = useLocale();
  const pathname = usePathname();
  const {
    ensureWaiverAccepted,
    waiverModal
  } = useVideoWaiver();
  const clearVideoQueryParams = useClearVideoQueryParams();
  const onAnyAction = (action: {
    type: string;
    payload: any;
  }) => {
    if (action.type === '@action.player.setLanguageMenuVisible') {
      const isOverlayFullscreen = window.matchMedia(PLAYER_FULLSCREEN_OVERLAY_MEDIA_QUERY).matches;
      if (action.payload === true && isOverlayFullscreen) {
        setIsCloseButtonVisible(false);
      } else {
        setIsCloseButtonVisible(true);
      }
    }
  };
  const closePlayer = () => {
    window.WebVideoPlayerDOM.destroy();
    setPlayerInstanceId(null);
    setLaunchVideoParams(null);
    setIsPlayerOpen(false);
    launchVideoPlayerPromise.current = null;
    clearVideoQueryParams();
  };
  const handleOnProgress = (progress: number, duration?: number) => {
    lastProgressRef.current = progress;
    reportVideoProgress(progress, duration);
    launchVideoParams?.props?.onProgress && launchVideoParams.props.onProgress(progress);
  };
  const trackExitVideo = (showPWE: boolean) => {
    const session = exitVideoSessionRef.current;
    exitVideoSessionRef.current = null;
    if (!session || !ampliDigital.isLoaded) return;
    const {
      videoGUID,
      duration: duration_0,
      analytics
    } = session;
    const playbackPosition = parseInt(lastProgressRef.current?.toString(), 10);
    ampliDigital.exitVideo({
      'app name': configPublic.general.appName,
      'video id': videoGUID,
      'video slug': analytics?.['video slug'] ?? '',
      'video title': analytics?.['video title'] ?? '',
      'video parent slug': analytics?.['video parent slug']?.toLowerCase() ?? 'none',
      'video duration': duration_0,
      'playback position': playbackPosition,
      'is completed': isCompletedRef.current,
      'show PWE': showPWE
    });
  };
  const handleOnClose = () => {
    const showPWE_0 = startPostWorkout();
    trackExitVideo(showPWE_0);
    closePlayer();
  };
  const handleOnCloseRef = useRef(handleOnClose);
  handleOnCloseRef.current = handleOnClose;
  const handlePlayerDialogClose = () => {
    if (waiverModal) {
      waiverModal.onDecline();
      return;
    }
    handleOnClose();
  };
  const playVideo = async (playVideoProps: PlayVideoProps) => {
    if (!isScriptLoaded || !window.WebVideoPlayerDOM) {
      throw new Error('WebVideoPlayerDOM not loaded');
    }
    setIsPlayerOpen(true);
    const getAccessToken = async () => {
      const newSession = await instrumentAction("AuthActions.getSession", getSession)();
      return {
        accessToken: newSession?.access_token,
        tokenType: newSession?.token_type
      };
    };

    // analytics must not reach the external player SDK.
    const {
      analytics: analytics_0,
      ...playerProps
    } = playVideoProps;

    // Call sites don't track play video themselves; it's emitted here.
    if (ampliDigital.isLoaded) {
      const screenName = analytics_0?.['screen name'] ?? getVideoPlayerScreenName(pathname ?? '');
      const tabName = analytics_0?.['tab name'] ?? getVideoPlayerTabName(pathname ?? '');
      ampliDigital.playVideo({
        'app name': configPublic.general.appName,
        'video id': playVideoProps.videoGUID,
        'screen name': screenName,
        'video slug': analytics_0?.['video slug'] ?? '',
        ...omitNullish({
          'tab name': tabName,
          'video title': analytics_0?.['video title'],
          catalog: analytics_0?.catalog,
          'video parent slug': analytics_0?.['video parent slug']?.toLowerCase() ?? 'none',
          'collection slug': analytics_0?.['collection slug']?.toLowerCase(),
          'video index': analytics_0?.['video index'],
          'suggested video index': analytics_0?.['suggested video index'],
          'is enrolled': analytics_0?.['is enrolled'],
          element: analytics_0?.element
        })
      });
    }
    const params = {
      selector: `#${VIDEO_PLAYER_CONTAINER_ID}`,
      props: {
        appName: configPublic.general.appName,
        appVersion: configPublic.general.appVersion,
        getAccessToken,
        onProgress: (progress_0: number) => handleOnProgress(progress_0, playVideoProps.duration),
        closeCallback: () => handleOnCloseRef.current(),
        onStop: () => {
          isCompletedRef.current = true;
        },
        userGUID: user?.guid,
        language: locale.codeSnakeCase,
        autoplay: playerProps.autoplay ?? true,
        onAnyAction,
        ...playerProps
      }
    };
    const isClearedToPlay = await ensureWaiverAccepted({
      videoGUID: playVideoProps.videoGUID,
      brandCode: getVideoBrandCode(playVideoProps)
    });
    if (!isClearedToPlay) {
      closePlayer();
      return;
    }
    lastProgressRef.current = 0;
    isCompletedRef.current = false;
    exitVideoSessionRef.current = {
      videoGUID: playVideoProps.videoGUID,
      duration: playVideoProps.duration ?? 0,
      analytics: analytics_0
    };
    setPostWorkoutEntityId(playVideoProps.entityId, playVideoProps.dayId);
    setLaunchVideoParams(params);
  };
  const playEntity = (content: CocoApi.PlayableEntity | CocoApi.CustomCollectionEntity, options?: PlayEntityOptions) => {
    const {
      analytics: analytics_1,
      ...playerOverrides
    } = options ?? {};
    const videoBrandCode = getVideoBrandCode({
      videoBrandCode: playerOverrides.videoBrandCode,
      brandCode: content.brandCode,
      isBODi: playerOverrides.isBODi
    });
    return playVideo({
      videoGUID: content.video?.videoId ?? '',
      entityId: content._id,
      recommendationId: content.recommendationId,
      isPromoVideo: content._type === 'promo',
      duration: content.durationEstimate,
      ...playerOverrides,
      videoBrandCode,
      analytics: analytics_1
    });
  };
  useEffect(() => {
    const launch = async () => {
      const instanceId = await window.WebVideoPlayerDOM.launch(launchVideoParams!);
      if (instanceId) setPlayerInstanceId(instanceId);
    };
    if (!!containerElement && !playerInstanceId && launchVideoParams && !launchVideoPlayerPromise.current) {
      launchVideoPlayerPromise.current = launch();
    }
  }, [launchVideoParams, playerInstanceId, containerElement]);
  useEffect(() => {
    return () => {
      if (playerInstanceId && window.WebVideoPlayerDOM?.destroy) {
        window.WebVideoPlayerDOM.destroy(playerInstanceId);
      }
    };
  }, [playerInstanceId]);
  const value: VideoPlayerContextType = {
    playVideo,
    playEntity,
    setScriptUrl,
    clearVideoQueryParams,
    isVideoPlayerOpen: isPlayerOpen
  };
  return <VideoPlayerContext.Provider value={value}>
      {children}
      <Dialog open={isPlayerOpen} onClose={handlePlayerDialogClose} className="relative z-50">
        <DialogPanel className="fixed inset-0 z-10 overflow-hidden bg-black/80 p-0">
          <div id={VIDEO_PLAYER_CONTAINER_ID} className="absolute inset-0 bg-bodi-black-text" />
          {!playerInstanceId && !waiverModal && <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <Image src="/images/loading.svg" height={88} width={88} alt="loading indicator" className="h-22 w-22 animate-spin [animation-timing-function:linear]" />
            </div>}
          {waiverModal && <VideoWaiver waiverAgreementBody={waiverModal.content} onWaiverAccepted={waiverModal.onAccept} onWaiverDeclined={waiverModal.onDecline} />}
          <AnimatePresence>
            {isCloseButtonVisible && <CloseButton key="close-player" as={ClosePlayer} onClick={handlePlayerDialogClose} />}
          </AnimatePresence>
        </DialogPanel>
      </Dialog>
      {scriptUrl && <Script async onReady={() => setIsScriptLoaded(true)} src={scriptUrl} />}
    </VideoPlayerContext.Provider>;
};
export const useVideoPlayer = scriptUrl => {
  const context = useContext(VideoPlayerContext);
  if (!context) {
    throw new Error("useVideoPlayer must be used within a VideoPlayerProvider");
  }
  if (scriptUrl && scriptUrl !== configPublic.videoPlayer.scriptUrl) {
    context.setScriptUrl(scriptUrl);
  }
  return context;
};
const ClosePlayer = React.forwardRef<HTMLDivElement, {
  onClick: () => void;
}>(function ClosePlayer(t0, ref) {
  const $ = _c(7);
  const {
    onClick
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      opacity: 1,
      transition: {
        delay: 0.3,
        duration: 0.3,
        ease: "easeInOut"
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      opacity: 0,
      transition: {
        duration: 0,
        ease: "easeInOut"
      }
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Image src={CloseWhite} title="close" alt="close" className="h-full w-full border-0 bg-no-repeat" />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== onClick || $[5] !== ref) {
    t5 = <motion.div ref={ref} initial={t1} animate={t2} exit={t3} className="absolute z-99 mt-8 ml-7 h-15 w-[8%] cursor-pointer p-2.5" onClick={onClick}>{t4}</motion.div>;
    $[4] = onClick;
    $[5] = ref;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
});